import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Check, MapPin, X, Lock, ShieldCheck,
  CheckCircle2, XCircle, Loader2, Home, RotateCcw, CreditCard,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { SuccessSound } from "./SiteSound";
import { submitBooking, createPaymentOrder, verifyPayment, paymentStatus, paymentFailed, loadRazorpay, formatApiError } from "../lib/api";
import { TIME_SLOTS, LOCATION_TYPES, nextDays } from "../config/defaults";
import { Field } from "./ui-bits";

const STEP_LABELS = ["Vehicle", "Plan", "Location", "Schedule", "Confirm", "Payment", "Done"];
const initial = { city: "", vehicle: "", plan_id: "", location_type: "Home", date: "", time_slot: "", address: "", name: "", phone: "", email: "", vehicle_number: "", notes: "", company_website: "" };

export default function BookingModal() {
  const { bookingOpen, closeBooking, config, bookingPrefill, customer } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [payState, setPayState] = useState("choose");
  const [payResult, setPayResult] = useState(null);
  const [order, setOrder] = useState(null);
  const [pendingNote, setPendingNote] = useState("");
  const days = useMemo(() => nextDays(7), []);

  useEffect(() => {
    if (bookingOpen) {
      setStep(0);
      setError("");
      setResultlessReset();
      setData({ ...initial, plan_id: bookingPrefill || "", name: customer?.name || "", phone: customer?.phone || "", email: customer?.email || "", city: customer?.city || "", vehicle: customer?.vehicle_type || "" });
      if (window.__lenis) window.__lenis.stop();
      document.body.style.overflow = "hidden";
    } else {
      if (window.__lenis) window.__lenis.start();
      document.body.style.overflow = "";
    }
  }, [bookingOpen, bookingPrefill, customer]);

  const setResultlessReset = () => { setBooking(null); setPayState("choose"); setPayResult(null); setOrder(null); setPendingNote(""); };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeBooking();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeBooking]);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const plan = config.customer_plans.find((p) => p.id === data.plan_id);

  const valid = [
    !!data.vehicle,
    !!data.plan_id,
    !!data.city && !!data.location_type && data.address.trim().length >= 8,
    !!data.date && !!data.time_slot,
    data.name.trim().length >= 2 && data.phone.trim().length >= 8,
  ];

  const createBooking = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await submitBooking(data);
      setBooking(res);
      setStep(5);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    if (!booking) return;
    setPayState("processing");
    setError("");
    try {
      const res = await createPaymentOrder(booking.booking_id);
      if (res.demo) {
        setOrder(res);
        setPayState("demo");
        return;
      }
      setOrder(res);
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: res.key_id,
        amount: res.amount_paise,
        currency: res.currency || "INR",
        order_id: res.order_id,
        description: "CARZA — Professional Car Care",
        prefill: { name: data.name, contact: data.phone, email: data.email || "" },
        notes: { booking_id: booking.booking_id, service_id: data.plan_id, service_name: booking.plan, vehicle_type: data.vehicle, booking_date: data.date, booking_time: data.time_slot },
        theme: { color: "#6D3DF5" },
        handler: async (r) => {
          const v = await verifyPayment({ booking_id: booking.booking_id, razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature }).catch(() => ({ ok: false, pending: true }));
          if (v.ok && !v.pending) {
            setPayResult(v);
            setPayState("success");
          } else {
            setPayState("pending");
            pollStatus(booking.booking_id);
          }
        },
        modal: {
          ondismiss: () => {
            setPayState("failed");
            paymentFailed(booking.booking_id, "checkout_dismissed");
          },
        },
      });
      rzp.on("payment.failed", () => {
        setPayState("failed");
        paymentFailed(booking.booking_id, "payment_failed");
      });
      rzp.open();
      setPayState("choose");
    } catch (e) {
      setPayState("failed");
      setError(formatApiError(e, "Could not start payment. Please try again."));
    }
  };

  const pollStatus = (bid, attempt = 0) => {
    if (attempt > 5) return;
    setTimeout(async () => {
      const s = await paymentStatus(bid).catch(() => null);
      if (s?.payment_status === "PAID") {
        setPayResult({ ok: true, ...s });
        setPayState("success");
      } else if (s?.payment_status !== "PAID" && attempt < 5) {
        if (payStateRef.current === "pending") pollStatus(bid, attempt + 1);
      }
    }, 3000);
  };

  const payStateRef = useRef(payState);
  payStateRef.current = payState;

  const next = () => { setError(""); setStep((s) => Math.min(4, s + 1)); };
  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };
  const onHome = () => { closeBooking(); navigate("/"); };

  return (
    <AnimatePresence>
      {bookingOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          role="dialog" aria-modal="true" aria-label="Book a car care" data-testid="booking-modal"
        >
          <div className="absolute inset-0 bg-[#0d0620]/75 backdrop-blur-sm" onClick={closeBooking} />
          <motion.div
            data-lenis-prevent
            className="relative flex max-h-[94vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-graphite sm:rounded-3xl"
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
          >
            <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="font-logo text-[11px] tracking-[0.2em] text-paper">carza</span>
                {step < 6 && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">
                    {step < 5 ? `Step ${step + 1} of 5 — ${STEP_LABELS[step]}` : STEP_LABELS[step]}
                  </span>
                )}
              </div>
              <button onClick={closeBooking} aria-label="Close booking" className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-mist hover:text-paper" data-testid="booking-close-btn">
                <X size={15} />
              </button>
            </div>
            {step < 6 && (
              <div className="h-[3px] w-full bg-white/5">
                <motion.div className="h-full bg-carza" animate={{ width: `${((step + 1) / 6) * 100}%` }} transition={{ duration: 0.4 }} />
              </div>
            )}

            <div className="overflow-y-auto overscroll-contain px-6 py-6" data-testid="booking-modal-body">
              <AnimatePresence mode="wait">
                <motion.div key={step + (payState || "")} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}>
                  {step === 0 && <StepVehicle config={config} data={data} set={set} />}
                  {step === 1 && <StepPlan config={config} data={data} set={set} />}
                  {step === 2 && <StepLocation config={config} data={data} set={set} />}
                  {step === 3 && <StepSchedule data={data} set={set} days={days} />}
                  {step === 4 && <StepConfirm data={data} set={set} plan={plan} error={error} loading={loading} onSubmit={createBooking} />}
                  {step === 5 && (
                    <PaymentStep
                      state={payState} data={data} booking={booking} order={order} plan={plan}
                      error={error} payResult={payResult} pendingNote={pendingNote}
                      onPay={payNow} onBackToBooking={() => setPayState("choose")}
                      onFinishDemo={closeBooking}
                      onHome={() => { closeBooking(); window.location.hash = ""; }}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {step <= 4 && (
              <div className="flex items-center justify-between gap-3 border-t border-white/5 px-6 py-4">
                <button onClick={back} disabled={step === 0} className="btn btn-ghost px-4 py-2 text-[13px] disabled:opacity-30" data-testid="booking-back-btn">
                  <ArrowLeft size={15} /> Back
                </button>
                <div className="flex items-center gap-3">
                  {plan && step >= 1 && (
                    <span className="font-mono text-sm text-carza-lite" data-testid="booking-estimate">₹{plan.price.toLocaleString("en-IN")}</span>
                  )}
                  {step < 4 && (
                    <button onClick={next} disabled={!valid[step]} className="btn btn-primary px-6 py-3 disabled:cursor-not-allowed disabled:opacity-40" data-testid="booking-next-btn">
                      Continue <ArrowRight size={15} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Radio = ({ active }) => (
  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${active ? "border-carza bg-carza" : "border-white/25"}`}>
    {active && <Check size={12} className="text-white" />}
  </span>
);

const OptionCard = ({ active, onClick, title, sub, right, disabled = false, testid }) => (
  <button
    onClick={onClick} disabled={disabled}
    className={`card-surface flex w-full items-center justify-between gap-3 rounded-2xl px-5 py-4 text-left transition-all ${
      active ? "!border-carza/60 shadow-[0_0_30px_-10px_rgba(124,77,255,0.5)]" : ""
    } ${disabled ? "opacity-40" : "hover:-translate-y-0.5"}`}
    data-testid={testid}
  >
    <span>
      <span className="block text-[15px] font-semibold text-paper">{title}</span>
      {sub && <span className="mt-0.5 block text-xs leading-relaxed text-mist">{sub}</span>}
    </span>
    {right}
  </button>
);

function StepVehicle({ config, data, set }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-2xl font-bold text-paper">Your vehicle type</h3>
      <p className="text-sm text-mist">Plans are built for everyday Indian cars — all types welcome.</p>
      {config.vehicles.map((v) => (
        <OptionCard
          key={v.name}
          testid={`booking-vehicle-${v.name.toLowerCase().split(" ")[0].replace("/", "-").trim()}`}
          active={data.vehicle === v.name}
          onClick={() => set("vehicle")(v.name)}
          title={v.name}
          right={<Radio active={data.vehicle === v.name} />}
        />
      ))}
    </div>
  );
}

function StepPlan({ config, data, set }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-2xl font-bold text-paper">Choose your plan</h3>
      {config.customer_plans.map((p) => (
        <OptionCard
          key={p.id}
          testid={`booking-plan-${p.id}`}
          active={data.plan_id === p.id}
          onClick={() => set("plan_id")(p.id)}
          title={p.name}
          sub={p.washes > 1 ? `${p.washes} washes · ${p.tagline}` : p.tagline}
          right={
            <span className="text-right">
              <span className="block font-mono text-sm font-semibold text-carza-lite">₹{p.price.toLocaleString("en-IN")}</span>
              {p.badge && <span className="font-mono text-[8px] uppercase tracking-widest text-mist-2">{p.badge}</span>}
            </span>
          }
        />
      ))}
    </div>
  );
}

function StepLocation({ config, data, set }) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-display text-2xl font-bold text-paper">Where should we arrive?</h3>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">City</span>
        <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto pb-1">
          {config.cities.map((c) => (
            <button
              key={c.name} disabled={!c.live} onClick={() => set("city")(c.name)}
              className={`shrink-0 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition-all ${
                data.city === c.name ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25 disabled:opacity-40"
              }`}
              data-testid={`booking-city-${c.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              {c.name}{!c.live && " · soon"}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Location type</span>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {LOCATION_TYPES.map((t) => (
            <button
              key={t} onClick={() => set("location_type")(t)}
              className={`rounded-xl border px-2 py-3 text-[13px] font-medium transition-all ${
                data.location_type === t ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"
              }`}
              data-testid={`booking-loctype-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <Field label="Address">
        <textarea
          className="field min-h-[90px] resize-none"
          value={data.address} onChange={(e) => set("address")(e.target.value)}
          placeholder="Flat / House no, Building, Street, Landmark…"
          data-testid="booking-step-location-input"
        />
      </Field>
      <Field label="Vehicle number" required={false}>
        <input className="field" value={data.vehicle_number} onChange={(e) => set("vehicle_number")(e.target.value)} placeholder="e.g. UP16 AB 1234 (optional)" data-testid="booking-vehicle-number-input" />
      </Field>
      <Field label="Notes for your partner" required={false}>
        <input className="field" value={data.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Optional — gate code, car colour, etc." data-testid="booking-notes-input" />
      </Field>
    </div>
  );
}

function StepSchedule({ data, set, days }) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-display text-2xl font-bold text-paper">Pick a date &amp; time</h3>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Date</span>
        <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto pb-1">
          {days.map((d, i) => (
            <button
              key={d} onClick={() => set("date")(d)}
              className={`shrink-0 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition-all ${
                data.date === d ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"
              }`}
              data-testid={`booking-date-${i}`}
            >
              {i === 0 ? `Today · ${d}` : d}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Time slot</span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {TIME_SLOTS.map((t) => (
            <button
              key={t} onClick={() => set("time_slot")(t)}
              className={`rounded-xl border px-4 py-3 text-[13px] font-medium transition-all ${
                data.time_slot === t ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"
              }`}
              data-testid={`booking-slot-${t.split(" ")[0]}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepConfirm({ data, set, plan, error, loading, onSubmit }) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-display text-2xl font-bold text-paper">Confirm your booking</h3>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-mist" data-testid="booking-summary">
        <div className="flex justify-between py-1"><span>Vehicle</span><span className="text-paper">{data.vehicle}{data.vehicle_number ? ` · ${data.vehicle_number}` : ""}</span></div>
        <div className="flex justify-between py-1"><span>Plan</span><span className="text-paper">{plan?.name}</span></div>
        <div className="flex justify-between py-1"><span>City</span><span className="text-paper">{data.city} · {data.location_type}</span></div>
        <div className="flex justify-between py-1"><span>When</span><span className="text-paper">{data.date}, {data.time_slot}</span></div>
        <div className="mt-2 flex justify-between border-t border-white/10 pt-2 font-semibold">
          <span>Plan price</span><span className="font-mono text-carza-lite">₹{plan?.price?.toLocaleString("en-IN")}</span>
        </div>
      </div>
      <Field label="Full name">
        <input className="field" value={data.name} onChange={(e) => set("name")(e.target.value)} placeholder="Your name" autoComplete="name" data-testid="booking-name-input" />
      </Field>
      <Field label="Mobile number">
        <input className="field" value={data.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="10-digit mobile number" inputMode="tel" autoComplete="tel" data-testid="booking-phone-input" />
      </Field>
      <input type="text" name="company_website" value={data.company_website} onChange={(e) => set("company_website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="booking-error">{error}</p>}
      <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-mist-2">
        <MapPin size={11} /> {data.address}
      </p>
      <button onClick={onSubmit} disabled={loading} className="btn btn-primary w-full py-4 disabled:opacity-50" data-testid="booking-modal-submit-btn">
        {loading ? "Creating booking…" : <>Continue to Payment <ArrowRight size={15} /></>}
      </button>
    </div>
  );
}

function PaymentStep({ state, data, booking, order, plan, error, payResult, onPay, onBackToBooking, onFinishDemo, onHome }) {
  const rows = [
    ["Booking ID", booking?.booking_id],
    ["Plan", `${plan?.name} — ${plan?.tagline}`],
    ["Vehicle", data.vehicle],
    ["Date & time", `${data.date} · ${data.time_slot}`],
    ["Location", data.address],
  ];

  if (state === "success") {
    return (
      <div className="flex flex-col items-center gap-5 py-2 text-center" data-testid="payment-success">
        <SuccessSound />
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }} className="flex h-16 w-16 items-center justify-center rounded-full bg-carza-dim text-carza-lite">
          <CheckCircle2 size={30} />
        </motion.div>
        <div>
          <h3 className="font-display text-2xl font-bold text-paper">Payment Successful ✓</h3>
          <p className="mt-2 text-sm text-mist">Your CARZA booking is confirmed.</p>
        </div>
        <div className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left text-sm" data-testid="payment-success-details">
          {[
            ...rows,
            ["Amount paid", `₹${(payResult?.amount_paid ?? booking?.estimated_price)?.toLocaleString?.("en-IN") || booking?.estimated_price}`],
            ["Payment ID", payResult?.razorpay_payment_id || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-6 border-t border-white/5 py-2.5 first:border-t-0">
              <span className="text-mist">{k}</span>
              <span className="text-right font-medium text-paper">{v}</span>
            </div>
          ))}
        </div>
        <div className="flex w-full items-center justify-center gap-1.5 text-[11px] text-mist-2">
          <Lock size={10} /> Secure payment powered by Razorpay
        </div>
        <button onClick={onHome} className="btn btn-primary w-full py-3.5" data-testid="payment-back-home-btn">
          Back to Home <Home size={15} />
        </button>
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="flex flex-col items-center gap-5 py-4 text-center" data-testid="payment-failed">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-400/10 text-red-400"><XCircle size={30} /></span>
        <div>
          <h3 className="font-display text-2xl font-bold text-paper">Payment was not completed.</h3>
          <p className="mt-2 text-sm text-mist">Your booking is saved — you can retry the payment right away.</p>
        </div>
        {error && <p className="w-full rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="payment-failed-error">{error}</p>}
        <button onClick={onPay} className="btn btn-primary w-full py-3.5" data-testid="payment-retry-btn">
          <RotateCcw size={15} /> Try Again
        </button>
        <button onClick={onPay} className="btn btn-outline w-full py-3.5" data-testid="payment-change-method-btn">
          <CreditCard size={15} /> Change Payment Method
        </button>
        <button onClick={onBackToBooking} className="btn btn-ghost w-full py-3" data-testid="payment-back-booking-btn">
          Back to Booking
        </button>
      </div>
    );
  }

  if (state === "pending") {
    return (
      <div className="flex flex-col items-center gap-5 py-6 text-center" data-testid="payment-pending">
        <Loader2 size={30} className="animate-spin text-carza-lite" />
        <div>
          <h3 className="font-display text-2xl font-bold text-paper">Payment verification in progress.</h3>
          <p className="mt-2 text-sm text-mist">We're confirming your payment with the bank. This usually takes a few seconds — your booking is saved as {booking?.booking_id}.</p>
        </div>
        <p className="text-[12px] text-mist-2">This screen updates automatically once payment is verified.</p>
      </div>
    );
  }

  if (state === "demo") {
    return (
      <div className="flex flex-col items-center gap-5 py-2 text-center" data-testid="payment-demo">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }} className="flex h-16 w-16 items-center justify-center rounded-full bg-carza-dim text-carza-lite">
          <BadgeCheck size={30} />
        </motion.div>
        <div>
          <h3 className="font-display text-2xl font-bold text-paper">Booking saved, {data.name.split(" ")[0]}.</h3>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-mist">
            Online payments are being activated for your city. Our team will confirm your slot and collect payment after the service — nothing to pay right now.
          </p>
        </div>
        <div className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left text-sm" data-testid="payment-demo-details">
          {[...rows, ["Amount (pay after service)", `₹${booking?.estimated_price?.toLocaleString?.("en-IN")}`]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-6 border-t border-white/5 py-2.5 first:border-t-0">
              <span className="text-mist">{k}</span>
              <span className="text-right font-medium text-paper">{v}</span>
            </div>
          ))}
        </div>
        <button onClick={onFinishDemo} className="btn btn-primary w-full py-3.5" data-testid="payment-demo-done-btn">Done</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="payment-summary">
      <h3 className="font-display text-2xl font-bold text-paper">Booking summary</h3>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-mist" data-testid="payment-summary-details">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 py-1.5">
            <span>{k}</span>
            <span className="text-right text-paper">{v}</span>
          </div>
        ))}
        <div className="mt-2 flex items-center justify-between border-t border-white/10 pt-3 font-semibold">
          <span className="text-paper">Amount payable</span>
          <span className="font-mono text-xl text-carza-lite" data-testid="payment-amount">₹{booking?.estimated_price?.toLocaleString?.("en-IN")}</span>
        </div>
      </div>
      {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="payment-error">{error}</p>}
      <button onClick={onPay} disabled={state === "processing"} className="btn btn-primary w-full py-4 disabled:opacity-60" data-testid="proceed-to-payment-btn">
        {state === "processing" ? "Opening secure checkout…" : <>Proceed to Secure Payment <Lock size={15} /></>}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-mist-2">
        <ShieldCheck size={12} className="text-carza-lite" /> Secure payment powered by Razorpay — UPI, cards, net banking &amp; more. CARZA never stores your card details.
      </p>
    </div>
  );
}
